import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import sortBy from 'lodash/sortBy';
import useSlingApi from '@sdk/api/useSlingApi';
import { ConversationSearchPayload } from '@sdk/messages/types/apiTypes';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';

import { getConversationSearchQueryKey } from '@web/conversations/utils/getQueryKey';
import { useConversationMappers } from '@web/conversations/hooks/useConversationMappers';
import { observeAnchor } from '@web/common/utils/observeAnchor';
import { Message } from '@web/conversations/types/Message';
const CONVERSATION_SEARCH_PAGE_SIZE = 20;

export const useConversationSearchList = () => {
  const { data: searchResults, ...infiniteQueryMetaData } = useGetConversationSearchResults();
  const { fetchPreviousPage, isFetchingPreviousPage, isLoading } = infiniteQueryMetaData;

  const [firstItemId, setFirstItemId] = useState<number | null>(null);
  const loadOlder = () => {
    setFirstItemId(results[0].id);
    fetchPreviousPage();
  };

  const isLoadingResults = isLoading || isFetchingPreviousPage;
  const results = useMemo(() => searchResults?.pages.flat(), [searchResults]);

  const { setLoadMoreNode } = useConversationSearchInfiniteLoading(loadOlder);

  return {
    infiniteQueryMetaData,
    firstItemId,
    isLoadingResults,
    loadOlder,
    results,
    setLoadMoreNode,
  };
};

export const useGetConversationSearchResults = () => {
  const { isReady, mapAPIMessageStrategy } = useConversationMappers();
  const { showErrorToast } = useToast();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('query');
  const queryKey = getConversationSearchQueryKey(searchQuery);
  const api = useSlingApi();

  const conversationId = id ? Number(id) : undefined;

  return useInfiniteQuery(
    queryKey,
    async ({ pageParam }) => {
      const config: ConversationSearchPayload = {
        text: searchQuery,
        page: pageParam?.pageNumber || 0,
        pageSize: CONVERSATION_SEARCH_PAGE_SIZE,
        conversationId,
      };

      const { data } = await api.conversation.search(config);
      return sortBy(
        data.map((message) => mapAPIMessageStrategy(message)),
        'postedAt'
      );
    },
    {
      onError: (error) => {
        showErrorToast(getErrorForToast(error));
      },
      cacheTime: 0,
      enabled: isReady,
      getPreviousPageParam: (oldestPage, allPages) => {
        const hasOlderSearchResults = oldestPage.length >= CONVERSATION_SEARCH_PAGE_SIZE;
        if (!hasOlderSearchResults) {
          return;
        }

        return { pageNumber: allPages.length };
      },
    }
  );
};

export const useSearchListScroll = (results: Message[], isLoading: boolean) => {
  const listFooterRef = useRef<HTMLDivElement | null>(null);
  const [firstItemRef, setFirstItemRef] = useState<HTMLDivElement | null>(null);
  const previousItemCount = useRef(0);

  useLayoutEffect(() => {
    if (listFooterRef.current && !isLoading) {
      listFooterRef.current.scrollIntoView();
    }
  }, [isLoading]);

  useEffect(() => {
    if (results && firstItemRef && previousItemCount.current !== results.length) {
      firstItemRef.scrollIntoView();
    }
  }, [results, firstItemRef]);

  return { listFooterRef, setFirstItemRef };
};

export const useConversationSearchInfiniteLoading = (fetchPreviousPage) => {
  const [loadMoreNode, setLoadMoreNode] = useState<HTMLDivElement | null>(null);
  useEffect(
    () => observeAnchor(loadMoreNode, (isIntersecting) => isIntersecting && fetchPreviousPage()),
    [fetchPreviousPage, loadMoreNode]
  );

  return { setLoadMoreNode };
};
